import { useSceneEvents } from "@/components/common/scene-events-context";
import {
  selectIElementsSlice,
  type OnlyIElementsStore,
} from "@/store/ielements-slice-selector";
import { selectModeName } from "@/store/mode-selectors";
import { curryAppSelector } from "@/store/reselect";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectPanoCameraTransform } from "@/utils/camera-transform";
import { type GUID } from "@faro-lotv/foundation";
import { isIElementImg360 } from "@faro-lotv/ielement-types";
import { uToPhi, vToTheta } from "@faro-lotv/lotv";
import {
  selectIElement,
  selectIElementWorldTransform,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { ReactNode, useCallback, type PropsWithChildren } from "react";
import { Quaternion, Vector3 } from "three";
import { selectBoundingBox } from "./ai-selectors";
import { setFocusedBoundingBox, setShowBoundingBoxes } from "./ai-slice";
import { ElementLink } from "./element-link";

type BoundingBoxLinkProps = {
  /** ID of the bounding box to navigate to when the link is clicked */
  boxId: GUID;
};

/**
 * @returns a clickable link that navigates to the panorama containing the given bounding box
 * and pans the camera to center on it.
 */
export function BoundingBoxLink({
  boxId,
  children,
}: PropsWithChildren<BoundingBoxLinkProps>): ReactNode {
  const { pendingLookAt, lookAt } = useSceneEvents();
  const dispatch = useAppDispatch();

  const boundingBox = useAppSelector(selectBoundingBox(boxId));

  const worldTransform = useAppSelector(
    selectTargetElementWorldTransform(boundingBox?.elementId),
  );

  const mode = useAppSelector(selectModeName);

  const panToBox = useCallback(() => {
    if (!boundingBox) return;

    const { x1, y1, x2, y2 } = boundingBox.boundingBox;
    const cy = (y1 + y2) / 2;

    // Convert UV coordinates to spherical angles
    const phi0 = uToPhi(x1 * 2 - 1);
    let phi1 = uToPhi(x2 * 2 - 1);

    // Handle wraparound: if the box spans the pano image border (x1 > x2),
    // phi0 < phi1, so subtract 2PI from phi1 to get the correct center.
    if (phi0 < phi1) {
      phi1 -= 2 * Math.PI;
    }

    const phi = (phi0 + phi1) / 2;
    const theta = vToTheta(1 - cy * 2);

    // Local unit direction using lotv sphereGeometry convention:
    // x = cos(phi)*cos(theta), y = sin(phi)*cos(theta), z = sin(theta)
    const localDir = new Vector3(
      Math.cos(phi) * Math.cos(theta),
      Math.sin(phi) * Math.cos(theta),
      Math.sin(theta),
    );

    // Rotate to world space and offset from pano position
    const target = new Vector3()
      .fromArray(worldTransform.position)
      .add(
        localDir.applyQuaternion(
          new Quaternion().fromArray(worldTransform.quaternion),
        ),
      );

    // Show only the clicked bounding box: focus it and turn off the "show all"
    // toggle so the other bounding boxes are hidden.
    dispatch(setShowBoundingBoxes(false));
    dispatch(setFocusedBoundingBox(boxId));

    if (mode === "walk") {
      // Move the camera if we are already in the correct pano
      lookAt.emit(target);
    } else {
      pendingLookAt.target = target.clone();
    }
  }, [
    boundingBox,
    dispatch,
    boxId,
    worldTransform.position,
    worldTransform.quaternion,
    mode,
    lookAt,
    pendingLookAt,
  ]);

  // Just render the link text if the boxId is invalid
  if (!boundingBox) return children;

  return (
    <ElementLink elementId={boundingBox.elementId} onClick={panToBox}>
      {children}
    </ElementLink>
  );
}

/**
 * @returns The world transform of the target iElement taking care of any offset if the target is a panorama image.
 * @param state The current app state
 * @param id The id of the iElement for which the transform should be computed
 */
const selectTargetElementWorldTransform = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, id: GUID | undefined) => id,
    ],
    (iElements, id) => {
      const iElement = selectIElement(id)({ iElements });
      if (!iElement || !isIElementImg360(iElement)) {
        return selectIElementWorldTransform(id)({ iElements });
      }
      return selectPanoCameraTransform(iElement)({ iElements });
    },
  ),
);
